import React, { useState, useEffect } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Business,
  LocalHospital,
  MedicalServices,
  HomeWork,
  KeyboardArrowDown
} from '@mui/icons-material';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import BookMyOpAPI from '../services/BookMyOpapi';

const OrganizationTypeFilter = ({ isScrolled = false, variant = 'desktop' }) => {
  const theme = useTheme();
  const { user, isAuthenticated, refreshProfile } = useBookMyOpAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [orgTypes, setOrgTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  // Get current org type preference from user profile
  const currentOrgType = user?.preferred_org_type;
  const currentOrgTypeName = user?.preferred_org_type_name;

  // Icon mapping for different organization types
  const getOrgTypeIcon = (orgTypeName, fontSize = '1rem') => {
    const name = orgTypeName?.toLowerCase() || '';
    const iconProps = { sx: { fontSize } };
    if (name.includes('hospital')) return <LocalHospital {...iconProps} />;
    if (name.includes('clinic')) return <MedicalServices {...iconProps} />;
    if (name.includes('diagnostic')) return <Business {...iconProps} />;
    if (name.includes('private')) return <HomeWork {...iconProps} />;
    return <Business {...iconProps} />;
  };

  // Fetch organization types on mount
  useEffect(() => {
    const fetchOrgTypes = async () => {
      setLoading(true);
      try {
        const response = await BookMyOpAPI.getOrganizationTypes();
        setOrgTypes(response);
      } catch (error) {
        console.error('Failed to fetch organization types:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load organization types',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrgTypes();
  }, []);

  // Set Private as default organization type if user is authenticated and has no preference
  useEffect(() => {
    const setDefaultOrgType = async () => {
      if (isAuthenticated && !currentOrgType && orgTypes.length > 0) {
        const privateOrgType = orgTypes.find(orgType => 
          orgType.name.toLowerCase().includes('private')
        );
        
        if (privateOrgType) {
          try {
            await BookMyOpAPI.updateOrganizationTypePreference(privateOrgType.id);
            await refreshProfile(true);
            
            // Refresh the entire app to ensure all components update
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } catch (error) {
            console.error('Failed to set default organization type:', error);
          }
        }
      }
    };

    setDefaultOrgType();
  }, [isAuthenticated, currentOrgType, orgTypes, refreshProfile]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOrgTypeSelect = async (orgType) => {
    const isPrivate = orgType.name?.toLowerCase().includes('private');

    // Coming soon for non-private options
    if (!isPrivate) {
      setComingSoonOpen(true);
      handleClose();
      return;
    }

    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login to set organization preference',
        severity: 'warning'
      });
      return;
    }

    setUpdating(true);
    try {
      await BookMyOpAPI.updateOrganizationTypePreference(orgType.id);
      
      // Refresh user profile to get updated data
      await refreshProfile(true);
      setSnackbar({
        open: true,
        message: `Filter set to ${orgType.name}`,
        severity: 'success'
      });
      
      handleClose();
      
      // Refresh the entire app to ensure all components update
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Failed to update organization type preference:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update preference',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getButtonText = () => {
    if (!isAuthenticated) return 'Org Type';
    if (currentOrgTypeName) return currentOrgTypeName;
    return 'Private';
  };

  const getButtonIcon = () => {
    if (updating) return <CircularProgress size={14} sx={{ color: 'inherit' }} />;
    if (currentOrgTypeName) return getOrgTypeIcon(currentOrgTypeName, '1rem');
    return <HomeWork sx={{ fontSize: '1rem' }} />;
  };

  const getButtonColor = () => {
    if (isScrolled) return 'primary';
    return 'inherit';
  };

  const getButtonSx = () => {
    const baseSx = {
      minWidth: variant === 'mobile' ? '0' : '140px',
      width: variant === 'mobile' ? '100%' : 'auto',
      height: variant === 'mobile' ? '30px' : '32px',
      justifyContent: 'space-between',
      fontSize: variant === 'mobile' ? '0.8rem' : '0.85rem',
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: variant === 'mobile' ? '100px' : '12px',
      px: variant === 'mobile' ? 1 : 1.25,
      transition: 'all 0.3s ease',
    };

    if (variant === 'mobile') {
      return {
        ...baseSx,
        color: 'text.primary',
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'primary.main',
        },
      };
    }

    return {
      ...baseSx,
      color: isScrolled ? 'primary.main' : 'white',
      backgroundColor: isScrolled 
        ? alpha(theme.palette.primary.main, 0.1)
        : 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      border: isScrolled
        ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        : '1px solid rgba(255, 255, 255, 0.3)',
      '&:hover': {
        backgroundColor: isScrolled
          ? alpha(theme.palette.primary.main, 0.15)
          : 'rgba(255, 255, 255, 0.25)',
        borderColor: isScrolled
          ? theme.palette.primary.main
          : 'rgba(255, 255, 255, 0.5)',
      },
    };
  };

  const getMenuSx = () => {
    return {
      mt: 1.5,
      minWidth: 280,
      maxWidth: 320,
      borderRadius: '16px',
      border: '1px solid',
      borderColor: alpha(theme.palette.primary.main, 0.1),
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      background: theme.palette.background.paper,
    };
  };

  const getLabelSx = () => {
    if (variant === 'mobile') {
      return {
        color: 'background.paper',
        fontSize: '0.65rem',
        mb: 0.4,
        fontWeight: 500,
        opacity: 0.8,
      };
    }
    return {
      color: isScrolled ? 'primary.main' : 'rgba(255, 255, 255, 0.8)',
      fontSize: '0.65rem',
      mb: 0.4,
    };
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="caption" sx={getLabelSx()}>
          Organisation
        </Typography>
        <Button
          startIcon={getButtonIcon()}
          endIcon={!updating && <KeyboardArrowDown sx={{ fontSize: '1rem' }} />}
          onClick={handleClick}
          disabled={updating || loading}
          sx={getButtonSx()}
        >
          <Box sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: variant === 'mobile' ? '100px' : '100px',
          }}>
            {getButtonText()}
          </Box>
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: getMenuSx()
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
            Filter by Organization Type
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isAuthenticated ? 'Choose your preferred organization type' : 'Login to set preferences'}
          </Typography>
        </Box>

        {/* Organization Types */}
        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Loading organization types...
            </Typography>
          </Box>
        ) : (
          orgTypes.map((orgType) => {
            const isPrivate = orgType.name?.toLowerCase().includes('private');
            return (
            <MenuItem
              key={orgType.id}
              onClick={isPrivate ? undefined : () => handleOrgTypeSelect(orgType)}
              disabled={isPrivate}
              sx={{
                py: 1.5,
                px: 2,
                gap: 1.5,
                borderRadius: '8px',
                m: 0.5,
                transition: 'all 0.3s ease',
                backgroundColor: currentOrgType === orgType.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                opacity: !isPrivate ? 0.6 : 1,
                cursor: !isPrivate ? 'not-allowed' : 'pointer',
                '&:hover': {
                  background: currentOrgType === orgType.id
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.primary.main, 0.08),
                  transform: isPrivate ? 'none' : 'translateX(4px)',
                },
                '&.Mui-disabled': {
                  opacity: 1,
                  cursor: 'default',
                  backgroundColor: currentOrgType === orgType.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': {
                    backgroundColor: currentOrgType === orgType.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    transform: 'none',
                  }
                }
              }}
            >
              <Box sx={{ color: currentOrgType === orgType.id ? theme.palette.primary.main : theme.palette.text.primary }}>
                {getOrgTypeIcon(orgType.name)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body2" 
                  fontWeight={currentOrgType === orgType.id ? 600 : 500}
                  sx={{ color: currentOrgType === orgType.id ? theme.palette.primary.main : theme.palette.text.primary }}
                >
                  {orgType.name}{!isPrivate && ' (Coming Soon)'}
                </Typography>
               
              </Box>
              {currentOrgType === orgType.id && (
                <Chip 
                  label="Active" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </MenuItem>
            );
          })
        )}

        {/* No org types message */}
        {!loading && orgTypes.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No organization types available
            </Typography>
          </Box>
        )}

        {/* Login prompt for unauthenticated users */}
        {!isAuthenticated && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Login to set your organization type preference
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Coming Soon Modal */}
      <Dialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minWidth: { xs: '90%', sm: '360px' }
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Coming soon
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.95rem' }}>
            Other organization types will be available soon. For now, please continue with Private.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setComingSoonOpen(false)}
            sx={{ borderRadius: '12px', fontWeight: 600, textTransform: 'none' }}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationTypeFilter;
